
import { ref, defineComponent, onMounted, onBeforeUnmount, computed } from "vue-demi";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import {
  API_TNotificationsShortListItem,
  API_TINotificationsList,
  TNotificationTag,
  TParams,
} from "@/models/notifications";
import debounce from "lodash.debounce";
import { SMUser } from "@/store";

export default defineComponent({
  name: "Notifications",

  components: {
    NotificationsUpdates: () =>
      import("@/modules/Notifications/NotificationsUpdates/NotificationsUpdates.vue"),
    NotificationsTasks: () =>
      import("@/modules/Notifications/NotificationsTasks/NotificationsTasks.vue"),
  },

  setup() {
    const newsParams = ref<TParams>({
      type: "news",
      offset: 0,
      tag: "",
    });

    const tasksParams = ref<TParams>({
      type: "tasks",
      offset: 0,
      tag: "",
    });

    const news = ref<API_TNotificationsShortListItem[]>([]);
    const tasks = ref<API_TNotificationsShortListItem[]>([]);

    const newsFinished = ref(false);
    const tasksFinished = ref(false);

    const isLoadingNews = ref(false);
    const isLoadingTasks = ref(false);

    const newsContainer = ref<HTMLElement | null>(null);
    const tasksContainer = ref<HTMLElement | null>(null);

    const hasMoreTasks = computed(() => !tasksFinished.value);
    const hasMoreNews = computed(() => !newsFinished.value);

    const isComplianceBlocked = computed(() => SMUser.isComplianceBlocked);

    const isNotSponsor = computed(() => SMUser.isNotSponsor);

    const isTasksHidden = computed(() => isComplianceBlocked.value || isNotSponsor.value);

    async function fetchAllNotifications() {
      try {
        const { data } = await axios.get(
          API_ROUTES.NOTIFICATIONS_LIST({ type: "all", offset: 0, tag: "" })
        );

        if (!data) return;

        const fullList = data as API_TINotificationsList;

        news.value = fullList.news || [];
        tasks.value = fullList.tasks || [];

        if (fullList.news.length < 10) newsFinished.value = true;
        if (fullList.tasks.length < 10) tasksFinished.value = true;

        if (isTasksHidden.value) {
          tasks.value = [];
        }
      } catch (error) {
        console.error(`Error fetching initial list:`, error);
      }
    }

    async function fetchNewsList() {
      if (isLoadingNews.value || newsFinished.value) return;

      isLoadingNews.value = true;

      try {
        const { data } = await axios.get(API_ROUTES.NOTIFICATIONS_LIST(newsParams.value));

        if (!data || (Array.isArray(data) && data.length === 0)) {
          newsFinished.value = true;
          return;
        }

        news.value.push(...(data as API_TNotificationsShortListItem[]));

        if ((data as API_TNotificationsShortListItem[]).length < 10) {
          newsFinished.value = true;
        }
      } catch (error) {
        console.error(`Error fetching news:`, error);
      } finally {
        isLoadingNews.value = false;
      }
    }

    async function fetchTasksList() {
      if (isLoadingTasks.value || tasksFinished.value) return;

      isLoadingTasks.value = true;

      try {
        const { data } = await axios.get(API_ROUTES.NOTIFICATIONS_LIST(tasksParams.value));

        if (!data || (Array.isArray(data) && data.length === 0)) {
          tasksFinished.value = true;
          return;
        }

        tasks.value.push(...(data as API_TNotificationsShortListItem[]));

        if ((data as API_TNotificationsShortListItem[]).length < 10) {
          tasksFinished.value = true;
        }
      } catch (error) {
        console.error(`Error fetching tasks:`, error);
      } finally {
        isLoadingTasks.value = false;
      }
    }

    const onScrollNews = debounce(() => {
      if (!newsContainer.value || isLoadingNews.value || newsFinished.value) return;

      const el = newsContainer.value;
      const scrollBottom = el.scrollTop + el.clientHeight;
      const scrollHeight = el.scrollHeight;

      if (scrollBottom >= scrollHeight * 0.9) {
        newsParams.value.offset += 10;
        fetchNewsList();
      }
    }, 200);

    const onScrollTasks = debounce(() => {
      if (!tasksContainer.value || isLoadingTasks.value || tasksFinished.value) return;

      const el = tasksContainer.value;
      const scrollBottom = el.scrollTop + el.clientHeight;
      const scrollHeight = el.scrollHeight;

      if (scrollBottom >= scrollHeight * 0.9) {
        tasksParams.value.offset += 10;
        fetchTasksList();
      }
    }, 200);

    function onNewsFilterChanged(newTag: TNotificationTag) {
      newsParams.value = {
        type: "news",
        offset: 0,
        tag: newTag,
      };
      news.value = [];
      newsFinished.value = false;
      fetchNewsList();
    }

    function onTasksFilterChanged(newTag: TNotificationTag) {
      if (isTasksHidden.value) {
        tasks.value = [];
        tasksFinished.value = true;

        return;
      }

      tasksParams.value = {
        type: "tasks",
        offset: 0,
        tag: newTag,
      };
      tasks.value = [];
      tasksFinished.value = false;

      fetchTasksList();
    }

    onMounted(() => {
      fetchAllNotifications();
    });

    onBeforeUnmount(() => {
      onScrollNews.cancel();
      onScrollTasks.cancel();
    });

    return {
      news,
      tasks,
      newsContainer,
      tasksContainer,
      onScrollNews,
      onScrollTasks,
      onNewsFilterChanged,
      onTasksFilterChanged,
      hasMoreTasks,
      hasMoreNews,
      tasksFinished,
      newsFinished,
      isLoadingNews,
      isLoadingTasks,
    };
  },
});
